import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { Loader2, AlertTriangle, Package as PackageIcon, Info } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { store } from '@/app/store';
import {
  completeOrderFulfillment,
  createOrderForCheckout,
  selectOrderItems,
  selectOrderState,
  selectShippingQuote,
  selectShippingQuoteStatus,
  selectShippingQuoteError,
  selectPackagingSummary,
  fetchShippingQuotes,
  hasPersistedOrderId,
  isShippingAddressComplete,
  syncOrderForCheckout,
  updateShippingAddress,
  buildCheckoutContactPayload,
} from '@/features/order/orderSlice';
import { getPaperTypes, getPrinters, PaperType, Printer } from '@/services/productService';
import OrderSummary from './OrderSummary';
import api from '@/services/api';
import ItemSelectionHeader from './ItemSelectionHeader';
import type { ItemSelectionOption } from './ItemSelectionHeader';
import { useTranslation } from 'react-i18next';
import { getStoredPricingLabel } from '@/components/order/hooks/useDebouncedPricingPreview';
import PaymentInstructionsModal from '@/components/liqpay/PaymentInstructionsModal';
import { prepareLiqPayCheckoutTarget, submitLiqPayForm } from '@/utils/liqpay';
import { analytics } from '@/services/analytics';
import { ORDER_FLOW_ITEM_STATUS_SET } from '@/types/orderStatus';
import {
  loadWalletSummary,
  selectWalletBalance,
  selectWalletCurrency,
} from '@/features/wallet/walletSlice';
import { selectMeasurementUnit, selectSelectedCurrency } from '@/features/preferences/preferencesSlice';
import { translateMeasurementUnitLabel } from '@/types/measurement';
import { convertInchesToUnit, unitPrecision } from '@/utils/measurement';
import { useSupervisor } from '@/hooks/useSupervisor';
import type { Order } from '@/types/supervisor';
import { transitionOrderStatusForUser, withdrawOrderBalance } from '@/services/orderService';
import { createWalletTopUpCheckout, fetchLiqPayExchangeRate } from '@/services/walletService';
import { logging } from '@/services/logging';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ConfirmAlertDialog from '@/components/ui/confirm-alert-dialog';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import {
  getShippingOffices,
  type DeliveryMode,
  type ShippingOfficeSuggestion,
  type ShippingQuoteOption,
} from '@/services/shippingService';
import { currencyOptionByCode } from '@/constants/currency';
import { discountApi, type PromotionPreview } from '@/services/discountApi';
import { getDefaultNovapostDeliveryMode, hasNovapostPresence } from '@/utils/novapost';
import { resolveLiqPayLanguage } from '@/utils/liqpayLanguage';
import { getPrintQualityWarningLabel } from './utils/printQualityWarning';

const UKRPOSHTA_FALLBACK_WARNING =
  'Exact shipping price not available; fixed average was charged; difference will be refunded to user internal wallet; if real price higher, company covers it';
const NOVAPOST_BRANCH_SELECTION_WARNING =
  'Nova Post quote is estimated until a specific branch is selected.';
const UKRPOSHTA_EU_COUNTRIES = new Set(['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK']);

const getShippingOptionKey = (option: ShippingQuoteOption): string => {
  const fingerprint = option.fingerprint?.trim();
  if (fingerprint) {
    return fingerprint;
  }
  return `${option.provider ?? 'UNKNOWN'}:${option.option}:${option.price.currency}:${option.price.amount}`;
};

const toShippingProviderLabel = (
  t: ReturnType<typeof useTranslation>['t'],
  provider?: string,
): string => {
  if (provider === 'NOVAPOST') {
    return t('orderSummary.shippingProviderNovaPost', 'Nova Post');
  }
  if (provider === 'UKRPOSHTA') {
    return t('orderSummary.shippingProviderUkrposhta', 'Ukrposhta');
  }
  return provider ?? t('orderSummary.shippingOptionUnknownProvider', 'Unknown provider');
};

const toShippingSpeedLabel = (
  t: ReturnType<typeof useTranslation>['t'],
  option?: ShippingQuoteOption,
): string | null => {
  if (!option) {
    return null;
  }
  return option.option === 'STANDARD'
    ? t('orderSummary.shippingOptionStandard', 'Standard delivery')
    : t('orderSummary.shippingOptionExpress', 'Express delivery');
};

const toReceiptPlaceLabel = (
  t: ReturnType<typeof useTranslation>['t'],
  mode: DeliveryMode,
): string => {
  switch (mode) {
    case 'COURIER':
      return t('orderSummary.placeOfReceiptAddress', 'Address');
    case 'BRANCH':
      return t('orderSummary.placeOfReceiptBranch', 'Branch');
    case 'LOCKER':
      return t('orderSummary.placeOfReceiptLocker', 'Parcel locker');
    case 'PICKUP_POINT':
      return t('orderSummary.placeOfReceiptPickupPoint', 'Pickup point');
    default:
      return mode;
  }
};

const normalizeOfficeCategory = (office: ShippingOfficeSuggestion): string => {
  return [
    office.type,
    office.label,
    office.address,
  ]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .trim()
    .toUpperCase();
};

const filterReceiptPlacesByMode = (
  offices: ReadonlyArray<ShippingOfficeSuggestion>,
  mode: DeliveryMode,
): ReadonlyArray<ShippingOfficeSuggestion> => {
  if (mode === 'COURIER') {
    return [];
  }

  const matches = offices.filter((office) => {
    const haystack = normalizeOfficeCategory(office);
    if (mode === 'BRANCH') {
      return haystack.includes('BRANCH') || haystack.includes('ВІДДІЛ') || haystack.includes('POSTBRANCH');
    }
    if (mode === 'LOCKER') {
      return haystack.includes('LOCKER')
        || haystack.includes('POSTOMAT')
        || haystack.includes('POST MAT')
        || haystack.includes('MACHINE');
    }
    return haystack.includes('PICKUP') || haystack.includes('POINT');
  });

  return matches;
};

const toPackageTypeLabel = (
  t: ReturnType<typeof useTranslation>['t'],
  packageType?: string,
): string | null => {
  if (!packageType) {
    return null;
  }

  const normalized = packageType.trim().toUpperCase().replace(/[\s-]+/g, '_');
  switch (normalized) {
    case 'DOCUMENT':
    case 'DOCUMENTS':
      return t('orderSummary.packageTypeDocuments', 'Documents');
    case 'LETTER':
      return t('orderSummary.packageTypeLetter', 'Letter');
    case 'SMALL_BAG':
      return t('orderSummary.packageTypeSmallBag', 'Small bag');
    case 'PARCEL':
      return t('orderSummary.packageTypeParcel', 'Parcel');
    case 'EMS':
      return t('orderSummary.packageTypeEms', 'EMS');
    case 'STANDARD':
      return t('orderSummary.packageTypeStandardParcel', 'Standard parcel');
    case 'EXPRESS':
      return t('orderSummary.packageTypeExpressParcel', 'Express parcel');
    default:
      return packageType;
  }
};

const normalizePackageType = (packageType?: string): string | null => {
  if (!packageType) {
    return null;
  }
  return packageType.trim().toUpperCase().replace(/[\s-]+/g, '_');
};

const formatEstimatedDeliveryRange = (
  t: ReturnType<typeof useTranslation>['t'],
  eta?: { minDays?: number; maxDays?: number } | null,
): string | null => {
  if (!eta) {
    return null;
  }

  const { minDays, maxDays } = eta;
  if (!minDays && !maxDays) {
    return null;
  }

  return t('orderSummary.shippingEtaHelper', 'Estimated delivery: {{min}}{{dash}}{{max}} days', {
    min: minDays ?? maxDays ?? 0,
    dash: minDays && maxDays ? '–' : '',
    max: maxDays ?? '',
  });
};

const inferEstimatedDeliveryWindow = (
  option: ShippingQuoteOption | undefined,
  destinationCountryCode?: string,
): { minDays: number; maxDays: number } | null => {
  if (!option) {
    return null;
  }

  const provider = option.provider?.trim().toUpperCase();
  const countryCode = destinationCountryCode?.trim().toUpperCase();
  const packageType = normalizePackageType(option.packageType);

  if (provider === 'NOVAPOST') {
    if (countryCode === 'UA') {
      return { minDays: 1, maxDays: 2 };
    }
    if (countryCode) {
      return { minDays: 3, maxDays: 7 };
    }
  }

  if (provider === 'UKRPOSHTA') {
    if (countryCode === 'UA') {
      if (packageType === 'DOCUMENT' || packageType === 'DOCUMENTS') {
        return { minDays: 1, maxDays: 3 };
      }
      return { minDays: 2, maxDays: 4 };
    }

    if (countryCode && UKRPOSHTA_EU_COUNTRIES.has(countryCode)) {
      return { minDays: 7, maxDays: 21 };
    }
  }

  return null;
};

const formatDetailedDestinationAddress = (
  address?: {
    street1?: string;
    street2?: string;
    city?: string;
    region?: string;
    postalCode?: string;
  } | null,
): string | null => {
  if (!address) {
    return null;
  }

  const line = [
    address.street1,
    address.street2,
    address.city,
    address.region,
    address.postalCode,
  ]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value) => value.length > 0)
    .join(', ');

  return line.length > 0 ? line : null;
};

type SummaryItem = {
  id?: string;
  uploadedFileId?: string;
  quantity?: number;
  paperTypeId?: string;
  printerId?: string;
  widthIn?: number;
  heightIn?: number;
  width?: number;
  height?: number;
  marginIn?: number;
  galleryWrapIn?: number;
};

interface Summary {
  items?: ReadonlyArray<SummaryItem>;
  paperTypeId?: string;
  printerId?: string;
}

const BALANCE_TOLERANCE = 0.01;

const roundCurrencyValue = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value * 100) / 100;
};

const convertCurrencyAmount = (value: number, rate: number): number => {
  if (!Number.isFinite(value) || !Number.isFinite(rate)) {
    return 0;
  }
  return roundCurrencyValue(value * rate);
};

const LIQPAY_SUPPORTED_CURRENCIES = ['USD', 'EUR', 'UAH'] as const;

const clampTopUpAmount = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 1;
  }
  const rounded = Math.round(value * 100) / 100;
  if (rounded <= 0) {
    return 1;
  }
  return rounded;
};

interface OrderSummaryContainerProps {
  imageId?: string;
  widthIn?: number;
  heightIn?: number;
  paperTypeId?: string;
  printerId?: string;
  originalWidthPx?: number;
  originalHeightPx?: number;
  summary?: Summary;
}


const OrderSummaryContainer: React.FC<OrderSummaryContainerProps> = (props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const orderItems = useAppSelector(selectOrderItems) || [];
  const orderState = useAppSelector(selectOrderState);
  const shippingAddress = orderState?.shippingAddress;
  const walletBalance = useAppSelector(selectWalletBalance);
  const walletBalanceCurrency = useAppSelector(selectWalletCurrency);
  const shippingQuote = useAppSelector(selectShippingQuote);
  const shippingQuoteStatus = useAppSelector(selectShippingQuoteStatus);
  const shippingQuoteError = useAppSelector(selectShippingQuoteError);
  const packagingSummary = useAppSelector(selectPackagingSummary);
  const measurementUnit = useAppSelector(selectMeasurementUnit);
  const selectedGlobalCurrency = useAppSelector(selectSelectedCurrency);
  const measurementPrecision = useMemo(() => unitPrecision(measurementUnit), [measurementUnit]);
  const measurementUnitLabel = useMemo(
    () => translateMeasurementUnitLabel(t, measurementUnit),
    [measurementUnit, t],
  );
  const { send: sendToSupervisor } = useSupervisor();
  const summary = props.summary;
  const summaryItems = summary?.items ?? [];
  const summaryPrimary = summaryItems[0];
  const [showShippingOptions, setShowShippingOptions] = useState(false);
  const [liqPayCurrencyDirty, setLiqPayCurrencyDirty] = useState(false);
  const initialLiqPayCurrency = useMemo(() => {
    const fallback = 'USD';
    if (selectedGlobalCurrency && LIQPAY_SUPPORTED_CURRENCIES.includes(selectedGlobalCurrency as typeof LIQPAY_SUPPORTED_CURRENCIES[number])) {
      return selectedGlobalCurrency;
    }
    return fallback;
  }, [selectedGlobalCurrency]);
  const [liqPayCurrency, setLiqPayCurrency] = useState<string>(initialLiqPayCurrency);
  const [userDiscountPercent, setUserDiscountPercent] = useState<number | null>(null);
  const [isPromoInputVisible, setIsPromoInputVisible] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [isApplyingPromoCode, setIsApplyingPromoCode] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);
  const [appliedPromoCodes, setAppliedPromoCodes] = useState<string[]>([]);
  const [promotionPreview, setPromotionPreview] = useState<PromotionPreview | null>(null);
  const hasInitializedPricingSync = useRef(false);
  const [shippingConversion, setShippingConversion] = useState<{
    rate: number;
    status: 'idle' | 'loading' | 'success' | 'error';
    error: string | null;
  }>({ rate: 1, status: 'idle', error: null });

  useEffect(() => {
    if (liqPayCurrencyDirty) {
      return;
    }
    const nextCurrency = LIQPAY_SUPPORTED_CURRENCIES.includes(selectedGlobalCurrency as typeof LIQPAY_SUPPORTED_CURRENCIES[number])
      ? selectedGlobalCurrency
      : 'USD';
    setLiqPayCurrency(nextCurrency);
  }, [selectedGlobalCurrency, liqPayCurrencyDirty]);

  const refreshUserDiscount = useCallback(async () => {
    try {
      const discount = await discountApi.getMyDiscount();
      const normalized = typeof discount?.percent === 'number' && Number.isFinite(discount.percent)
        ? Math.max(0, Math.min(100, discount.percent))
        : null;
      setUserDiscountPercent(normalized && normalized > 0 ? normalized : null);
      return normalized && normalized > 0 ? normalized : null;
    } catch (error) {
      logging.warn('Failed to fetch user discount for order summary', { error });
      setUserDiscountPercent(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshUserDiscount().then(() => undefined);
  }, [refreshUserDiscount]);

  // Determine the most likely order-item identifier and associated file metadata.
  const allSelectableItems = useMemo(() => {
    return orderItems.filter((entry): entry is typeof entry & { id: string } => (
      Boolean(entry && typeof entry.id === 'string' && entry.id.trim().length > 0)
    ));
  }, [orderItems]);

  const preferredOrderItemId = summaryPrimary?.id;
  const preferredUploadedFileId = summaryPrimary?.uploadedFileId ?? props.imageId;
  const matchedItem = preferredOrderItemId
    ? allSelectableItems.find((item) => item.id === preferredOrderItemId)
    : preferredUploadedFileId
      ? allSelectableItems.find((item) => item.uploadedFileId === preferredUploadedFileId)
      : allSelectableItems[0];

  const orderedItems = useMemo(() => {
    return orderItems.filter((entry): entry is typeof entry & { id: string } => {
      if (!entry || typeof entry.id !== 'string') {
        return false;
      }
      const status = typeof entry.status === 'string' ? entry.status.toUpperCase() : '';
      return ORDER_FLOW_ITEM_STATUS_SET.has(status as import('@/types/orderStatus').OrderItemStatus);
    });
  }, [orderItems]);

  const toggleShippingOptions = useCallback(() => {
    setShowShippingOptions((prev) => !prev);
  }, []);

  const [activeOrderedId, setActiveOrderedId] = useState<string | null>(null);

  useEffect(() => {
    if (orderedItems.length === 0) {
      if (activeOrderedId !== null) {
        setActiveOrderedId(null);
      }
      return;
    }
    if (activeOrderedId && orderedItems.some(item => item.id === activeOrderedId)) {
      return;
    }
    const nextId = orderedItems[0]?.id ?? null;
    if (nextId !== activeOrderedId) {
      setActiveOrderedId(nextId);
    }
  }, [activeOrderedId, orderedItems]);

  const orderedHeaderOptions = useMemo<readonly ItemSelectionOption[]>(() => {
    return orderedItems.map((ordered, index) => ({
      key: ordered.id ?? `ordered-${index}`,
      label: t('canvas.steps.itemNumber', 'Item {{index}}', { index: index + 1 }),
      uploadedFileId: ordered.uploadedFileId ?? undefined,
      priceLabel: getStoredPricingLabel({ item: ordered, t }) ?? undefined,
      warningLabel: getPrintQualityWarningLabel(ordered, t),
    }));
  }, [orderedItems, t]);

  const activeOrderedItem = orderedItems.find(item => item.id === activeOrderedId) ?? null;
  const resolvedItem = activeOrderedItem ?? matchedItem;

  const [paperTypes, setPaperTypes] = useState<PaperType[] | null>(null);
  const [printers, setPrinters] = useState<Printer[] | null>(null);
  const [paperTypeName, setPaperTypeName] = useState<string | undefined>();
  const [printerName, setPrinterName] = useState<string | undefined>();
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [imageDims, setImageDims] = useState<{ width?: number; height?: number }>({});
  const [imageInches, setImageInches] = useState<{ widthIn?: number; heightIn?: number }>({});

  const fulfillmentMode = orderState?.fulfillmentMode ?? 'SHIPPING';
  const isPickupMode = fulfillmentMode === 'PICKUP';

  const shippingAddressText = (() => {
    if (isPickupMode) {
      const a = orderState?.shippingAddress;
      const contact = [a?.firstName, a?.lastName].filter(Boolean).join(' ').trim();
      const phone = a?.phone?.trim();
      return [
        t('orderSummary.pickupAddressSummary', 'Self pickup from the studio'),
        contact || undefined,
        phone || undefined,
      ].filter(Boolean).join(', ');
    }

    const a = orderState?.shippingAddress;
    if (!a) return undefined;
    const lines: string[] = [];
    const name = [a.firstName, a.lastName].filter(Boolean).join(' ');
    if (name) lines.push(name);
    const street = [a.street1, a.street2].filter(Boolean).join(', ');
    if (street) lines.push(street);
    const cityLine = [a.city, a.region, a.postalCode].filter(Boolean).join(', ');
    if (cityLine) lines.push(cityLine);
    if (a.countryCode) lines.push(a.countryCode);
    if (a.phone) lines.push(a.phone);
    return lines.join(', ');
  })();

  const effectivePaperTypeId = resolvedItem?.paperTypeId
    ?? props.paperTypeId
    ?? (summary?.paperTypeId ?? summary?.items?.[0]?.paperTypeId);
  const effectivePrinterId = resolvedItem?.printerId
    ?? props.printerId
    ?? (summary?.printerId ?? summary?.items?.[0]?.printerId);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const [papers, prns] = await Promise.all([
          getPaperTypes().catch(() => []),
          getPrinters().catch(() => []),
        ]);
        if (!mounted) return;
        setPaperTypes(papers);
        setPrinters(prns);
      } catch (e) {
        // handled above per-call; keep UI resilient
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (paperTypes && effectivePaperTypeId) {
      const found = paperTypes.find(p => p.id === effectivePaperTypeId);
      setPaperTypeName(found ? `${found.brand ? found.brand + ' ' : ''}${found.name}` : undefined);
    }
  }, [paperTypes, effectivePaperTypeId]);

  useEffect(() => {
    if (printers && effectivePrinterId) {
      const found = printers.find(p => p.id === effectivePrinterId);
      setPrinterName(found ? `${found.brand ? found.brand + ' ' : ''}${found.name}` : undefined);
    }
  }, [printers, effectivePrinterId]);

  const widthInchesStored = resolvedItem?.widthIn ?? props.widthIn ?? summaryPrimary?.widthIn ?? imageInches.widthIn;
  const heightInchesStored = resolvedItem?.heightIn ?? props.heightIn ?? summaryPrimary?.heightIn ?? imageInches.heightIn;
  const width = resolvedItem?.width ?? summaryPrimary?.width ?? widthInchesStored;
  const height = resolvedItem?.height ?? summaryPrimary?.height ?? heightInchesStored;
  const imageId = resolvedItem?.uploadedFileId ?? summaryPrimary?.uploadedFileId ?? props.imageId ?? undefined;

  // Fetch public image URL and dimensions
  useEffect(() => {
    let mounted = true;
    if (!imageId) {
      setImageUrl(undefined);
      setImageDims({});
      setImageInches({});
      return;
    }
    api
      .get(`/upload/${imageId}`)
      .then((res) => {
        if (!mounted) return;
        const data = res.data || {};
        if (data.url) setImageUrl(data.url);
        setImageDims({ width: data.width, height: data.height });
        const widthIn = typeof data.widthIn === 'number' ? data.widthIn as number : undefined;
        const heightIn = typeof data.heightIn === 'number' ? data.heightIn as number : undefined;
        setImageInches({ widthIn, heightIn });
      })
      .catch(() => {
        if (!mounted) return;
        setImageUrl(undefined);
      });
    return () => {
      mounted = false;
    };
  }, [imageId]);

  const isPaperConfigured = typeof effectivePaperTypeId === 'string' && effectivePaperTypeId.trim().length > 0;
  const isPrinterConfigured = typeof effectivePrinterId === 'string' && effectivePrinterId.trim().length > 0;
  const isPrintSizeConfigured = typeof widthInchesStored === 'number'
    && widthInchesStored > 0
    && typeof heightInchesStored === 'number'
    && heightInchesStored > 0;
  const hasShippingAddress = isPickupMode || Boolean(orderState?.shippingAddress);
  const orderId = orderState?.id ?? null;

  const [selectedShippingOptionKey, setSelectedShippingOptionKey] = useState<string | null>(null);
  const [hasManualShippingSelection, setHasManualShippingSelection] = useState(false);
  const [manualShippingSelectionIdentity, setManualShippingSelectionIdentity] = useState<string | null>(null);

  const filteredShippingOptions = useMemo(() => {
    const options = shippingQuote?.options ?? [];
    const grouped = new Map<string, ShippingQuoteOption[]>();
    for (const option of options) {
      const providerKey = option.provider ?? shippingQuote?.provider ?? 'UNKNOWN';
      const existing = grouped.get(providerKey) ?? [];
      existing.push(option);
      grouped.set(providerKey, existing);
    }

    const result: ShippingQuoteOption[] = [];
    for (const providerOptions of grouped.values()) {
      const standardOption = providerOptions.find((option) => option.option === 'STANDARD');
      for (const option of providerOptions) {
        if (option.option !== 'EXPRESS') {
          result.push(option);
          continue;
        }

        if ((option.provider ?? shippingQuote?.provider ?? null) === 'NOVAPOST') {
          result.push(option);
          continue;
        }

        if (!standardOption) {
          result.push(option);
          continue;
        }

        if (option.status === 'FALLBACK') {
          continue;
        }

        const sameRate = option.price.amount === standardOption.price.amount
          && option.price.currency === standardOption.price.currency;

        if (!sameRate) {
          result.push(option);
        }
      }
    }
    return result;
  }, [shippingQuote]);

  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [isInitiatingTopUp, setIsInitiatingTopUp] = useState(false);
  const [topUpError, setTopUpError] = useState<string | null>(null);
  const [isPollingPayment, setIsPollingPayment] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBalanceReadyPromptOpen, setIsBalanceReadyPromptOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const novapostSupportsOfficeSelection = hasNovapostPresence(shippingAddress?.countryCode);
  const defaultNovapostDeliveryMode = getDefaultNovapostDeliveryMode(shippingAddress?.countryCode);

  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>(shippingAddress?.deliveryMode || defaultNovapostDeliveryMode);
  const [selectedWarehouse, setSelectedWarehouse] = useState<ShippingOfficeSuggestion | null>(null);
  const [warehouses, setWarehouses] = useState<ShippingOfficeSuggestion[]>([]);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);
  const lastNovapostQuoteRequestKey = useRef<string | null>(null);

  useEffect(() => {
    if (shippingAddress?.deliveryMode && !novapostSupportsOfficeSelection && shippingAddress.deliveryMode !== 'COURIER') {
      dispatch(updateShippingAddress({
        deliveryMode: 'COURIER',
        novaposhtaWarehouseId: undefined,
      }));
    }
  }, [dispatch, novapostSupportsOfficeSelection, shippingAddress?.deliveryMode]);

  useEffect(() => {
    if (!shippingAddress?.deliveryMode) {
      setDeliveryMode((current) => (current === defaultNovapostDeliveryMode ? current : defaultNovapostDeliveryMode));
      return;
    }
    const nextMode = !novapostSupportsOfficeSelection && shippingAddress.deliveryMode !== 'COURIER'
      ? 'COURIER'
      : shippingAddress.deliveryMode;
    setDeliveryMode((current) => (current === nextMode ? current : nextMode));
  }, [defaultNovapostDeliveryMode, novapostSupportsOfficeSelection, shippingAddress?.deliveryMode]);

  const clientPricingSummary = useMemo(() => {
    const items = orderedItems;
    let total = 0;
    let currency: string | null = null;
    let pricedCount = 0;
    let hasMismatch = false;
    let hasInvalid = false;

    for (const item of items) {
      const price = item?.price;
      if (!price) {
        continue;
      }
      const { value, currency: itemCurrency } = price;
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        continue;
      }
      if (typeof itemCurrency !== 'string' || itemCurrency.trim().length === 0) {
        hasInvalid = true;
        continue;
      }

      if (!currency) {
        currency = itemCurrency;
      } else if (currency !== itemCurrency) {
        hasMismatch = true;
      }

      const quantity = typeof item?.quantity === 'number' && Number.isFinite(item.quantity)
        ? Math.min(99, Math.max(1, Math.round(item.quantity)))
        : 1;

      total += value * quantity;
      pricedCount += 1;
    }

    return {
      total,
      currency,
      pricedCount,
      totalItems: items.length,
      hasMismatch,
      hasInvalid,
      hasUnpricedItems: pricedCount < items.length,
    };
  }, [orderedItems]);

  const pricingSummary = useMemo(() => {
    const serverSummary = orderState?.pricingSummary;
    if (!serverSummary) {
      return clientPricingSummary;
    }

    const itemsSubtotal = typeof serverSummary.itemsSubtotal === 'number'
      ? serverSummary.itemsSubtotal
      : typeof serverSummary.total === 'number'
        ? serverSummary.total
        : clientPricingSummary.total;

    const pricedCount = typeof serverSummary.pricedItemCount === 'number'
      ? serverSummary.pricedItemCount
      : clientPricingSummary.pricedCount;

    const totalItems = typeof serverSummary.itemCount === 'number'
      ? serverSummary.itemCount
      : clientPricingSummary.totalItems;

    return {
      total: itemsSubtotal,
      currency: serverSummary.currency ?? clientPricingSummary.currency,
      pricedCount,
      totalItems,
      hasMismatch: clientPricingSummary.hasMismatch,
      hasInvalid: clientPricingSummary.hasInvalid,
      hasUnpricedItems: serverSummary.hasUnpricedItems ?? (pricedCount < totalItems),
    };
  }, [clientPricingSummary, orderState?.pricingSummary]);

  const orderCurrency = pricingSummary.currency ?? 'USD';

  const [shippingOptionConversions, setShippingOptionConversions] = useState<Record<string, {
    rate: number;
    status: 'idle' | 'loading' | 'success' | 'error';
  }>>({});

  useEffect(() => {
    const uniqueCurrencies = Array.from(new Set(
      filteredShippingOptions
        .map((option) => option.price.currency?.trim().toUpperCase())
        .filter((currency): currency is string => Boolean(currency)),
    ));

    if (uniqueCurrencies.length === 0) {
      setShippingOptionConversions({});
      return;
    }

    let cancelled = false;

    setShippingOptionConversions((prev) => {
      const next: typeof prev = {};
      for (const currency of uniqueCurrencies) {
        if (currency === orderCurrency) {
          next[currency] = { rate: 1, status: 'success' };
          continue;
        }
        next[currency] = prev[currency] ?? { rate: 1, status: 'idle' };
      }
      return next;
    });

    void Promise.all(uniqueCurrencies.map(async (currency) => {
      if (currency === orderCurrency) {
        return { currency, rate: 1, status: 'success' as const };
      }

      try {
        const result = await fetchLiqPayExchangeRate(currency, orderCurrency);
        return { currency, rate: result.rate, status: 'success' as const };
      } catch (error) {
        logging.error('Failed to resolve shipping option exchange rate', { error, currency, orderCurrency });
        return { currency, rate: 1, status: 'error' as const };
      }
    })).then((results) => {
      if (cancelled) {
        return;
      }
      setShippingOptionConversions((prev) => {
        const next = { ...prev };
        for (const result of results) {
          next[result.currency] = { rate: result.rate, status: result.status };
        }
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [filteredShippingOptions, orderCurrency]);

  const getComparableShippingAmount = useCallback((option: ShippingQuoteOption): number => {
    const currency = option.price.currency?.trim().toUpperCase();
    if (!currency || currency === orderCurrency) {
      return option.price.amount;
    }
    const conversion = shippingOptionConversions[currency];
    const rate = conversion?.status === 'success' && Number.isFinite(conversion.rate) && conversion.rate > 0
      ? conversion.rate
      : 1;
    return convertCurrencyAmount(option.price.amount, rate);
  }, [orderCurrency, shippingOptionConversions]);

  const sortedShippingOptions = useMemo(() => {
    return [...filteredShippingOptions].sort((left, right) => {
      if (left.status !== right.status) {
        return left.status === 'OK' ? -1 : 1;
      }

      const amountDelta = getComparableShippingAmount(left) - getComparableShippingAmount(right);
      if (amountDelta !== 0) {
        return amountDelta;
      }

      if (left.option !== right.option) {
        return left.option === 'STANDARD' ? -1 : 1;
      }

      return getShippingOptionKey(left).localeCompare(getShippingOptionKey(right));
    });
  }, [filteredShippingOptions, getComparableShippingAmount]);

  const getAutoSelectedShippingOption = useCallback((options: ReadonlyArray<ShippingQuoteOption>): ShippingQuoteOption | undefined => {
    const liveOptions = options.filter((option) => option.status !== 'FALLBACK');
    const candidates = liveOptions.length > 0 ? liveOptions : [...options];

    return [...candidates].sort((left, right) => {
      const amountDelta = getComparableShippingAmount(left) - getComparableShippingAmount(right);
      if (amountDelta !== 0) {
        return amountDelta;
      }
      if (left.status !== right.status) {
        return left.status === 'OK' ? -1 : 1;
      }
      if (left.option !== right.option) {
        return left.option === 'STANDARD' ? -1 : 1;
      }
      return getShippingOptionKey(left).localeCompare(getShippingOptionKey(right));
    })[0];
  }, [getComparableShippingAmount]);

  useEffect(() => {
    if (sortedShippingOptions.length === 0) {
      setSelectedShippingOptionKey(null);
      setHasManualShippingSelection(false);
      setManualShippingSelectionIdentity(null);
      return;
    }

    setSelectedShippingOptionKey((current) => {
      if (hasManualShippingSelection) {
        if (current && sortedShippingOptions.some((option) => getShippingOptionKey(option) === current)) {
          return current;
        }
        if (manualShippingSelectionIdentity) {
          const matchingOption = sortedShippingOptions.find((option) => (
            `${option.provider ?? shippingQuote?.provider ?? 'UNKNOWN'}:${option.option}` === manualShippingSelectionIdentity
          ));
          if (matchingOption) {
            return getShippingOptionKey(matchingOption);
          }
        }
      }
      const next = getAutoSelectedShippingOption(sortedShippingOptions) ?? sortedShippingOptions[0];
      return getShippingOptionKey(next);
    });
  }, [sortedShippingOptions, getAutoSelectedShippingOption, hasManualShippingSelection, manualShippingSelectionIdentity, shippingQuote?.provider]);

  useEffect(() => {
    setIsLoadingBalance(true);
    void dispatch(loadWalletSummary()).finally(() => setIsLoadingBalance(false));
  }, [dispatch, orderId]);

  useEffect(() => {
    const selectedOption = sortedShippingOptions.find((option) => getShippingOptionKey(option) === selectedShippingOptionKey)
      ?? sortedShippingOptions[0];
    const isSelectedNovapost = (selectedOption?.provider ?? shippingQuote?.provider) === 'NOVAPOST';
    const requiresOfficeSelection = novapostSupportsOfficeSelection && deliveryMode !== 'COURIER';
    if (!isSelectedNovapost || !shippingAddress?.novaposhtaCityRef || !requiresOfficeSelection) {
      setWarehouses([]);
      return;
    }

    let cancelled = false;
    setIsLoadingWarehouses(true);

    const timeoutId = setTimeout(() => {
      getShippingOffices('NOVAPOST', {
        countryCode: shippingAddress.countryCode,
        cityId: shippingAddress.novaposhtaCityRef!,
      })
        .then((results) => {
          if (!cancelled) {
            setWarehouses([...filterReceiptPlacesByMode(results, deliveryMode)]);
          }
        })
        .catch((err) => {
          logging.error('Failed to fetch warehouses', { err });
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoadingWarehouses(false);
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [sortedShippingOptions, selectedShippingOptionKey, shippingAddress?.novaposhtaCityRef, shippingAddress?.countryCode, deliveryMode, novapostSupportsOfficeSelection, shippingQuote?.provider]);

  useEffect(() => {
    if (deliveryMode === 'COURIER') {
      if (selectedWarehouse) {
        setSelectedWarehouse(null);
      }
      return;
    }

    const persistedWarehouseId = shippingAddress?.novaposhtaWarehouseId?.trim();
    if (!persistedWarehouseId) {
      return;
    }

    const matchingWarehouse = warehouses.find((warehouse) => warehouse.id === persistedWarehouseId) ?? null;
    if (!matchingWarehouse) {
      return;
    }

    setSelectedWarehouse((current) => (current?.id === matchingWarehouse.id ? current : matchingWarehouse));
  }, [deliveryMode, selectedWarehouse, shippingAddress?.novaposhtaWarehouseId, warehouses]);

  const handleDeliveryModeChange = (mode: DeliveryMode) => {
    if (!novapostSupportsOfficeSelection && mode !== 'COURIER') {
      return;
    }
    setDeliveryMode(mode);
    setSelectedWarehouse(null);
    dispatch(updateShippingAddress({
      deliveryMode: mode,
      novaposhtaWarehouseId: undefined,
    }));
    analytics.track('ui.order_summary_delivery_mode_change', { orderId, mode });
  };

  const handleWarehouseSelect = useCallback((warehouse: ShippingOfficeSuggestion) => {
    setSelectedWarehouse(warehouse);
    dispatch(updateShippingAddress({
      deliveryMode,
      novaposhtaWarehouseId: warehouse.id,
    }));
    analytics.track('ui.order_summary_warehouse_select', { orderId, warehouseId: warehouse.id });
  }, [analytics, deliveryMode, dispatch, orderId]);

  const warehouseSelectOptions = useMemo<ReadonlyArray<SearchableSelectOption>>(() => {
    return warehouses.map((warehouse) => ({
      value: warehouse.id,
      label: warehouse.address ? `${warehouse.label} - ${warehouse.address}` : warehouse.label,
    }));
  }, [warehouses]);

  useEffect(() => {
    const selectedOption = sortedShippingOptions.find((option) => getShippingOptionKey(option) === selectedShippingOptionKey)
      ?? sortedShippingOptions[0];
    const isSelectedNovapost = (selectedOption?.provider ?? shippingQuote?.provider) === 'NOVAPOST';
    if (isPickupMode || !isSelectedNovapost || !hasShippingAddress || shippingQuoteStatus === 'loading') {
      if (!isSelectedNovapost) {
        lastNovapostQuoteRequestKey.current = null;
      }
      return;
    }

    const requiresOfficeSelection = novapostSupportsOfficeSelection && deliveryMode !== 'COURIER';
    const officeId = requiresOfficeSelection ? selectedWarehouse?.id : undefined;
    const requestKey = `${deliveryMode}:${officeId ?? ''}`;
    if (lastNovapostQuoteRequestKey.current === requestKey) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      lastNovapostQuoteRequestKey.current = requestKey;
      void dispatch(fetchShippingQuotes({
        deliveryMode,
        officeId,
      }));
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [
    dispatch,
    deliveryMode,
    hasShippingAddress,
    novapostSupportsOfficeSelection,
    selectedShippingOptionKey,
    selectedWarehouse?.id,
    shippingQuote?.provider,
    shippingQuoteStatus,
    sortedShippingOptions,
    isPickupMode,
  ]);

  const activeShippingOption: ShippingQuoteOption | undefined = useMemo(() => {
    return sortedShippingOptions.find((option) => getShippingOptionKey(option) === selectedShippingOptionKey)
      ?? sortedShippingOptions[0];
  }, [sortedShippingOptions, selectedShippingOptionKey]);
  const activeShippingEta = useMemo(
    () => activeShippingOption?.eta ?? inferEstimatedDeliveryWindow(activeShippingOption, shippingAddress?.countryCode),
    [activeShippingOption, shippingAddress?.countryCode],
  );
  const shippingEstimateTooltip = useMemo(
    () => t(
      'orderSummary.shippingEtaTooltip',
      'The exact numbers you can fine-tune from the official delivery terms pages and your real parcel history.',
    ),
    [t],
  );

  const shippingCurrency = activeShippingOption?.price.currency ?? pricingSummary.currency ?? 'USD';
  const resolvedShippingCost = activeShippingOption?.price.amount ?? 0;
  const hasResolvedShippingCost = shippingQuoteStatus === 'succeeded' && Boolean(activeShippingOption);
  const shippingCostInOrderCurrency = useMemo(() => {
    if (!hasResolvedShippingCost) {
      return 0;
    }
    const rate = Number.isFinite(shippingConversion.rate) && shippingConversion.rate > 0 ? shippingConversion.rate : 1;
    const converted = resolvedShippingCost * rate;
    return roundCurrencyValue(converted);
  }, [hasResolvedShippingCost, resolvedShippingCost, shippingConversion.rate]);

  const shippingCost = isPickupMode ? 0 : shippingCostInOrderCurrency;
  const isWaitingForShippingQuote = !isPickupMode && !hasResolvedShippingCost;

  const orderTotal = useMemo(
    () => roundCurrencyValue(Math.max(0, pricingSummary.total + shippingCost - (promotionPreview?.promotionDiscountTotalUsd ?? 0))),
    [pricingSummary.total, promotionPreview?.promotionDiscountTotalUsd, shippingCost],
  );

  const balanceDelta = orderTotal - walletBalance;
  const hasEnoughBalance = balanceDelta <= BALANCE_TOLERANCE;

  const walletCurrency = walletBalanceCurrency ?? orderCurrency;

  useEffect(() => {
    if (!hasResolvedShippingCost) {
      setShippingConversion({ rate: 1, status: 'idle', error: null });
      return;
    }
    if (!shippingCurrency || shippingCurrency === orderCurrency) {
      setShippingConversion({ rate: 1, status: 'success', error: null });
      return;
    }

    let cancelled = false;
    setShippingConversion((prev) => ({ ...prev, status: 'loading', error: null }));
    fetchLiqPayExchangeRate(shippingCurrency, orderCurrency)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setShippingConversion({ rate: result.rate, status: 'success', error: null });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        logging.error('Failed to resolve shipping exchange rate', { error, shippingCurrency, orderCurrency });
        const message = error instanceof Error ? error.message : t('orderSummary.shippingConversionFallback', 'Unable to convert shipping currency. Showing original amount.');
        setShippingConversion({ rate: 1, status: 'error', error: message });
      });

    return () => {
      cancelled = true;
    };
  }, [hasResolvedShippingCost, orderCurrency, shippingCurrency, t]);

  const [orderConversion, setOrderConversion] = useState<{ rate: number; status: 'idle' | 'loading' | 'success' | 'error'; source: string | null; effectiveDate: string | null; error: string | null }>({
    rate: 1,
    status: 'idle',
    source: null,
    effectiveDate: null,
    error: null,
  });

  const [walletConversion, setWalletConversion] = useState<{ rate: number; status: 'idle' | 'loading' | 'success' | 'error'; source: string | null; effectiveDate: string | null; error: string | null }>({
    rate: 1,
    status: 'idle',
    source: null,
    effectiveDate: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    if (!liqPayCurrency || liqPayCurrency === orderCurrency) {
      setOrderConversion({ rate: 1, status: 'success', source: null, effectiveDate: null, error: null });
      return () => {
        cancelled = true;
      };
    }
    setOrderConversion((prev) => ({ ...prev, status: 'loading', error: null }));
    fetchLiqPayExchangeRate(orderCurrency, liqPayCurrency)
      .then((result) => {
        if (cancelled) return;
        setOrderConversion({
          rate: result.rate,
          status: 'success',
          source: result.source,
          effectiveDate: result.effectiveDate,
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        logging.error('Failed to fetch LiqPay exchange rate (order)', { error });
        const message = error instanceof Error ? error.message : t('orderSummary.liqPayRateError', 'Failed to fetch exchange rate. Using base currency amount.');
        setOrderConversion({ rate: 1, status: 'error', source: null, effectiveDate: null, error: message });
      });
    return () => {
      cancelled = true;
    };
  }, [liqPayCurrency, orderCurrency, t]);

  useEffect(() => {
    const baseCurrency = walletCurrency ?? orderCurrency;
    let cancelled = false;
    if (!liqPayCurrency) {
      return () => {
        cancelled = true;
      };
    }
    if (baseCurrency === liqPayCurrency) {
      setWalletConversion({ rate: 1, status: 'success', source: null, effectiveDate: null, error: null });
      return () => {
        cancelled = true;
      };
    }
    if (baseCurrency === orderCurrency) {
      setWalletConversion({
        rate: orderConversion.rate,
        status: orderConversion.status,
        source: orderConversion.source,
        effectiveDate: orderConversion.effectiveDate,
        error: orderConversion.error,
      });
      return () => {
        cancelled = true;
      };
    }
    setWalletConversion((prev) => ({ ...prev, status: 'loading', error: null }));
    fetchLiqPayExchangeRate(baseCurrency, liqPayCurrency)
      .then((result) => {
        if (cancelled) return;
        setWalletConversion({
          rate: result.rate,
          status: 'success',
          source: result.source,
          effectiveDate: result.effectiveDate,
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        logging.error('Failed to fetch LiqPay exchange rate (wallet)', { error });
        const message = error instanceof Error ? error.message : t('orderSummary.liqPayRateError', 'Failed to fetch exchange rate. Using base currency amount.');
        setWalletConversion({ rate: 1, status: 'error', source: null, effectiveDate: null, error: message });
      });
    return () => {
      cancelled = true;
    };
  }, [liqPayCurrency, walletCurrency, orderCurrency, orderConversion, t]);

  const formatLiqPayCurrency = useCallback((value: number) => {
    try {
      return new Intl.NumberFormat(i18n.language ?? undefined, {
        style: 'currency',
        currency: liqPayCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (error) {
      logging.warn('Failed to format LiqPay currency value', { error });
      return `${liqPayCurrency} ${value.toFixed(2)}`;
    }
  }, [i18n.language, liqPayCurrency]);

  const liqPayOrderTotal = useMemo(
    () => convertCurrencyAmount(orderTotal, orderConversion.rate),
    [orderConversion.rate, orderTotal],
  );
  const liqPayWalletBalance = useMemo(
    () => convertCurrencyAmount(walletBalance, walletConversion.rate),
    [walletBalance, walletConversion.rate],
  );

  const liqPayBalanceTolerance = useMemo(() => {
    const effectiveRate = Number.isFinite(orderConversion.rate) && orderConversion.rate > 0
      ? orderConversion.rate
      : 1;
    const convertedTolerance = roundCurrencyValue(BALANCE_TOLERANCE * effectiveRate);
    return Math.max(roundCurrencyValue(BALANCE_TOLERANCE), convertedTolerance || 0.01);
  }, [orderConversion.rate]);

  const liqPayShortfallRaw = useMemo(
    () => Math.max(0, liqPayOrderTotal - liqPayWalletBalance),
    [liqPayOrderTotal, liqPayWalletBalance],
  );

  const liqPayShortfall = useMemo(() => {
    if (liqPayShortfallRaw <= liqPayBalanceTolerance) {
      return 0;
    }
    return roundCurrencyValue(liqPayShortfallRaw);
  }, [liqPayBalanceTolerance, liqPayShortfallRaw]);

  const formattedLiqPayOrderTotal = useMemo(() => formatLiqPayCurrency(liqPayOrderTotal), [formatLiqPayCurrency, liqPayOrderTotal]);
  const formattedLiqPayWalletBalance = useMemo(() => formatLiqPayCurrency(liqPayWalletBalance), [formatLiqPayCurrency, liqPayWalletBalance]);
  const formattedLiqPayShortfall = useMemo(() => formatLiqPayCurrency(liqPayShortfall), [formatLiqPayCurrency, liqPayShortfall]);

  const formattedOrderTotalBaseCurrency = useMemo(() => {
    try {
      return new Intl.NumberFormat(i18n.language ?? undefined, {
        style: 'currency',
        currency: orderCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(orderTotal);
    } catch (error) {
      logging.warn('Failed to format order total in base currency', { error });
      return `${orderCurrency} ${orderTotal.toFixed(2)}`;
    }
  }, [i18n.language, orderCurrency, orderTotal]);

  const exchangeRateInfo = useMemo(() => {
    if (orderConversion.status !== 'success' || orderCurrency === liqPayCurrency) {
      return null;
    }
    const formattedRate = formatLiqPayCurrency(orderConversion.rate);
    const effectiveDateLabel = orderConversion.effectiveDate ? new Date(orderConversion.effectiveDate).toLocaleDateString(i18n.language ?? undefined) : null;
    return t('orderSummary.paymentExchangeRate', '1 {{base}} ≈ {{converted}} ({{source}}{{date}})', {
      base: orderCurrency,
      converted: formattedRate,
      source: orderConversion.source ?? 'NBU',
      date: effectiveDateLabel ? ` · ${effectiveDateLabel}` : '',
    });
  }, [orderConversion, orderCurrency, liqPayCurrency, formatLiqPayCurrency, i18n.language, t]);

  const currencyControlHelper = useMemo(() => {
    if (orderConversion.status === 'loading' || walletConversion.status === 'loading') {
      return t('orderSummary.liqPayRateLoading', 'Fetching exchange rate…');
    }
    if (orderConversion.status === 'error') {
      return orderConversion.error;
    }
    if (walletConversion.status === 'error') {
      return walletConversion.error;
    }
    return null;
  }, [orderConversion, walletConversion, t]);

  const liqPayCurrencyOptions = useMemo(
    () => LIQPAY_SUPPORTED_CURRENCIES.map((code) => {
      const option = currencyOptionByCode[code];
      const label = option ? `${option.code} · ${option.label}` : code;
      return {
        value: code,
        label,
      };
    }),
    [],
  );

  const paymentCurrencyControl = useMemo(() => ({
    label: t('orderSummary.liqPayCurrencyLabel', 'Payment currency'),
    value: liqPayCurrency,
    onChange: (value: string) => {
      setLiqPayCurrencyDirty(true);
      setLiqPayCurrency(value);
    },
    options: liqPayCurrencyOptions,
    helperText: currencyControlHelper,
    loading: orderConversion.status === 'loading' || walletConversion.status === 'loading',
  }), [currencyControlHelper, liqPayCurrency, liqPayCurrencyOptions, orderConversion.status, walletConversion.status, t]);

  const paymentExchangeRateInfo = useMemo(() => {
    if (orderConversion.status === 'error') {
      return orderConversion.error;
    }
    if (walletConversion.status === 'error' && walletCurrency !== orderCurrency) {
      return walletConversion.error;
    }
    return exchangeRateInfo;
  }, [orderConversion, walletConversion, exchangeRateInfo, walletCurrency, orderCurrency]);

  const liqPayPaymentHelperText = hasEnoughBalance
    ? t('orderSummary.confirmHelper', 'Review details, then confirm to lock in your order.')
    : t('orderSummary.topUpHelper', 'Top up the exact amount needed to finish checkout.');

  const formatCurrency = useCallback(
    (value: number, currencyOverride?: string) => {
      try {
        const currency = currencyOverride ?? pricingSummary.currency ?? 'USD';
        const formatter = new Intl.NumberFormat(i18n.language ?? undefined, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return formatter.format(value);
      } catch (error) {
        logging.warn('Failed to format currency value', { error });
        return `$${value.toFixed(2)}`;
      }
    },
    [i18n.language, pricingSummary.currency],
  );

  const handleRefreshShippingQuote = useCallback(() => {
    if (isPickupMode || shippingQuoteStatus === 'loading') {
      return;
    }
    analytics.track('button: ui.order_summary_refresh_shipping_quote_click', { orderId });
    const isSelectedNovapost = (activeShippingOption?.provider ?? shippingQuote?.provider) === 'NOVAPOST';
    const officeId = deliveryMode !== 'COURIER' ? selectedWarehouse?.id : undefined;
    void dispatch(fetchShippingQuotes(isSelectedNovapost ? { deliveryMode, officeId } : undefined));
  }, [activeShippingOption?.provider, analytics, deliveryMode, dispatch, isPickupMode, orderId, selectedWarehouse?.id, shippingQuote?.provider, shippingQuoteStatus]);

  const refreshPersistedOrderPricing = useCallback(async () => {
    const latestState = store.getState();
    if (latestState.order.checkoutPersistenceInFlight) {
      return;
    }

    if (hasPersistedOrderId(orderId)) {
      await dispatch(syncOrderForCheckout()).unwrap();
      return;
    }
    await dispatch(createOrderForCheckout()).unwrap();
  }, [dispatch, orderId]);

  useEffect(() => {
    if (hasInitializedPricingSync.current) {
      return;
    }
    if (orderedItems.length === 0) {
      return;
    }

    hasInitializedPricingSync.current = true;
    void refreshPersistedOrderPricing().catch((syncError) => {
      logging.warn('[OrderSummaryContainer] Initial order pricing refresh failed', { syncError });
    });
  }, [orderedItems.length, refreshPersistedOrderPricing]);

  useEffect(() => {
    if (isPickupMode || shippingQuoteStatus !== 'idle') {
      return;
    }
    if (!hasPersistedOrderId(orderId)) {
      return;
    }
    if (!isShippingAddressComplete(shippingAddress)) {
      return;
    }
    if (orderedItems.length === 0) {
      return;
    }

    void dispatch(fetchShippingQuotes());
  }, [dispatch, isPickupMode, orderId, orderedItems.length, shippingAddress, shippingQuoteStatus]);

  const previewPromoCodes = useCallback(async (codes: string[]) => {
    if (!orderId || codes.length === 0) {
      setPromotionPreview(null);
      return null;
    }

    const preview = await discountApi.previewPromotionCodes(orderId, codes);
    setPromotionPreview(preview);
    return preview;
  }, [orderId]);

  useEffect(() => {
    if (appliedPromoCodes.length === 0) {
      return;
    }

    void previewPromoCodes(appliedPromoCodes).catch((error) => {
      logging.warn('Failed to refresh promotion preview', { error });
    });
  }, [appliedPromoCodes, previewPromoCodes, pricingSummary.total]);

  const handleApplyPromoCode = useCallback(async () => {
    const normalizedCode = promoCode.trim().toUpperCase();
    if (!normalizedCode || isApplyingPromoCode) {
      return;
    }

    setPromoCodeError(null);
    setIsApplyingPromoCode(true);
    try {
      const nextCodes = Array.from(new Set([...appliedPromoCodes, normalizedCode]));
      const preview = await previewPromoCodes(nextCodes);
      setAppliedPromoCodes(preview?.codes ?? nextCodes);
      setPromoCode('');
      setPromoCodeError(null);
      setIsPromoInputVisible(false);
    } catch (error) {
      const responseMessage = isAxiosError(error) ? error.response?.data?.message : null;
      const fallbackMessage = error instanceof Error ? error.message : null;
      setPromoCodeError(
        (typeof responseMessage === 'string' && responseMessage.trim().length > 0
          ? responseMessage
          : fallbackMessage) || t('orderSummary.promoCodeInvalid', 'Invalid or expired promo code'),
      );
    } finally {
      setIsApplyingPromoCode(false);
    }
  }, [appliedPromoCodes, isApplyingPromoCode, previewPromoCodes, promoCode, t]);

  const handleRemovePromoCode = useCallback((code: string) => {
    const nextCodes = appliedPromoCodes.filter((entry) => entry !== code);
    setAppliedPromoCodes(nextCodes);
    setPromoCodeError(null);
    if (nextCodes.length === 0) {
      setPromotionPreview(null);
      return;
    }

    void previewPromoCodes(nextCodes).catch((error) => {
      logging.error('Failed to refresh promotion preview after removing code', { error });
      setPromotionPreview(null);
      setPromoCodeError(t('orderSummary.promoCodeInvalid', 'Invalid or expired promo code'));
    });
  }, [appliedPromoCodes, previewPromoCodes, t]);

  const handleSelectShippingOption = useCallback((option: ShippingQuoteOption) => {
    setSelectedShippingOptionKey(getShippingOptionKey(option));
    setHasManualShippingSelection(true);
    setManualShippingSelectionIdentity(`${option.provider ?? shippingQuote?.provider ?? 'UNKNOWN'}:${option.option}`);
    analytics.track('button: ui.order_summary_shipping_option_select', {
      orderId,
      option: option.option,
      provider: option.provider ?? null,
    });
  }, [analytics, orderId, shippingQuote?.provider]);

  const handleSelectShippingOptionAndHide = useCallback((option: ShippingQuoteOption) => {
    setSelectedShippingOptionKey(getShippingOptionKey(option));
    setHasManualShippingSelection(true);
    setManualShippingSelectionIdentity(`${option.provider ?? shippingQuote?.provider ?? 'UNKNOWN'}:${option.option}`);
    setShowShippingOptions(false);
    analytics.track('button: ui.order_summary_shipping_option_double_click', {
      orderId,
      option: option.option,
      provider: option.provider ?? null,
    });
  }, [analytics, orderId, shippingQuote?.provider]);

  const renderShippingOptionBadge = useCallback((option: ShippingQuoteOption) => {
    if (option.status === 'FALLBACK') {
      return (
        <Badge variant="destructive" className="uppercase tracking-wide">
          {t('orderSummary.shippingFallbackBadge', 'Fallback')}
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="uppercase tracking-wide">
        {t('orderSummary.shippingLiveBadge', 'Live rate')}
      </Badge>
    );
  }, [t]);

  const buildPackagingHelper = useCallback((): string | null => {
    if (!packagingSummary) {
      return null;
    }
    const { totalWeightGrams, packageDimensions } = packagingSummary;
    if (!packageDimensions) {
      return null;
    }
    const dimensionLabel = `${packageDimensions.lengthIn.toFixed(1)} × ${packageDimensions.widthIn.toFixed(1)} × ${packageDimensions.heightIn.toFixed(1)} in`;
    return t('orderSummary.shippingPackageDetails', 'Package weight {{weight}}g · Dimensions {{dimensions}}', {
      weight: Math.round(totalWeightGrams),
      dimensions: dimensionLabel,
    });
  }, [packagingSummary, t]);

  const translateShippingWarning = useCallback((warning: string): string => {
    const normalized = warning.trim();
    if (normalized === UKRPOSHTA_FALLBACK_WARNING) {
      return t(
        'orderSummary.shippingFallbackWarning',
        'Exact shipping price not available; fixed average was charged; difference will be refunded to user internal wallet; if real price higher, company covers it',
      );
    }
    return warning;
  }, [t]);

  let shippingCostLabel: string | null = null;
  let shippingCostSummary: React.ReactNode = null;
  let shippingCostHelper: string | null = null;
  const hasFallbackShippingSignal = filteredShippingOptions.some((option) => {
    if (option.status === 'FALLBACK') {
      return true;
    }
    return option.warnings?.some((warning) => warning.trim() === UKRPOSHTA_FALLBACK_WARNING) ?? false;
  });
  const showShippingOptionsWarning = !isPickupMode && hasShippingAddress
    && (
      shippingQuoteStatus === 'failed'
      || (shippingQuoteStatus === 'succeeded' && filteredShippingOptions.length === 0)
      || (shippingQuoteStatus === 'succeeded' && hasFallbackShippingSignal)
    );
  const isRefreshingShippingQuote = shippingQuoteStatus === 'loading' && filteredShippingOptions.length > 0;

  if (isPickupMode) {
    shippingCostLabel = t('orderSummary.pickupCostLabel', 'Free');
    shippingCostHelper = t('orderSummary.pickupHelper', 'You will collect the printed art yourself. Delivery fee is not charged.');
    shippingCostSummary = (
      <div className="flex flex-wrap justify-end gap-1.5">
        <Badge variant="default" className="bg-primary/90 text-primary-foreground">
          {t('orderSummary.fulfillmentPickup', 'Self pickup')}
        </Badge>
      </div>
    );
  } else if (!hasShippingAddress) {
    shippingCostLabel = t('orderSummary.shippingNeedsAddress', 'Add address to calculate');
    shippingCostHelper = t('orderSummary.shippingNeedsAddressHelper', 'Complete the shipping address to get delivery rates.');
  } else if (hasResolvedShippingCost) {
    const currenciesDiffer = shippingCurrency !== orderCurrency;
    const shouldUseConverted = shippingConversion.status === 'success' || !currenciesDiffer;
    const convertedLabel = formatCurrency(shippingCostInOrderCurrency, orderCurrency);
    const originalLabel = formatCurrency(resolvedShippingCost, shippingCurrency);
    const selectedShipmentParts = [
      toShippingProviderLabel(t, activeShippingOption?.provider ?? shippingQuote?.provider),
      toShippingSpeedLabel(t, activeShippingOption),
      toPackageTypeLabel(t, activeShippingOption?.packageType),
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    shippingCostLabel = shouldUseConverted ? convertedLabel : originalLabel;
    shippingCostSummary = selectedShipmentParts.length > 0 ? (
      <div className="flex flex-wrap justify-end gap-1.5">
        {selectedShipmentParts.map((part, index) => (
          <Badge
            key={`${part}-${index}`}
            variant={index === 0 ? 'default' : 'outline'}
            className={index === 0 ? 'bg-primary/90 text-primary-foreground' : 'border-primary/30 text-foreground'}
          >
            {part}
          </Badge>
        ))}
      </div>
    ) : null;
    const helperNotes: string[] = [];
    if (activeShippingOption?.status === 'FALLBACK') {
      helperNotes.push(t('orderSummary.shippingFallbackHelper', 'Using fallback pricing due to provider issues.'));
    }
    if (isRefreshingShippingQuote) {
      helperNotes.push(t('orderSummary.shippingRefreshingHelper', 'Refreshing delivery rates…'));
    }
    if (shippingConversion.status === 'loading') {
      helperNotes.push(t('orderSummary.shippingConversionLoading', 'Converting shipping amount…'));
    }
    if (shippingConversion.status === 'error' && shippingConversion.error) {
      helperNotes.push(shippingConversion.error);
    }
    if (shippingQuote?.cache?.hit) {
      helperNotes.push(t('orderSummary.shippingCacheHit', 'This price was cached from the last request.'));
    } else if (shippingQuote?.cache) {
      helperNotes.push(
        t('orderSummary.shippingCacheMiss', 'This price was not cached from the last request.'));
    }
    if (activeShippingOption?.warnings && activeShippingOption.warnings.length > 0) {
      helperNotes.push(activeShippingOption.warnings.map(translateShippingWarning).join(' · '));
    }
    const packagingNote = buildPackagingHelper();
    if (packagingNote) {
      helperNotes.push(packagingNote);
    }
    if (shippingQuote?.cache?.hit) {
      helperNotes.push(t('orderSummary.shippingCacheHit', 'Served from cache · refresh for latest pricing.'));
    }
    const estimatedDeliveryLabel = formatEstimatedDeliveryRange(t, activeShippingEta);
    if (estimatedDeliveryLabel) {
      helperNotes.push(estimatedDeliveryLabel);
    }
    shippingCostHelper = helperNotes.length > 0 ? helperNotes.join(' • ') : null;
  } else if (shippingQuoteStatus === 'loading') {
    shippingCostLabel = t('orderSummary.shippingLoading', 'Fetching rates…');
    shippingCostHelper = t('orderSummary.shippingLoadingHelper', 'Contacting delivery providers for current pricing.');
  } else if (shippingQuoteStatus === 'failed') {
    shippingCostLabel = t('orderSummary.shippingUnavailable', 'Unavailable');
    shippingCostHelper = shippingQuoteError
      ? t('orderSummary.shippingErrorHelper', 'We could not load shipping rates ({{message}}). Try refreshing.', {
          message: shippingQuoteError,
        })
      : t('orderSummary.shippingErrorGeneric', 'We could not load shipping rates. Try refreshing.');
  } else {
    shippingCostLabel = t('orderSummary.shippingPending', 'Awaiting rate calculation');
    shippingCostHelper = t('orderSummary.shippingPendingHelper', 'Rates will appear once calculation completes.');
  }

  const renderShippingOptions = () => {
    const isRefreshingShippingOptions = shippingQuoteStatus === 'loading' && sortedShippingOptions.length > 0;

    if (!hasShippingAddress) {
      return (
        <CardDescription>
          {t('orderSummary.shippingNeedsAddressDescription', 'Provide a complete shipping address to view delivery options.')}
        </CardDescription>
      );
    }

    if (shippingQuoteStatus === 'loading' && sortedShippingOptions.length === 0) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('orderSummary.shippingLoadingInline', 'Fetching the latest delivery rates…')}
        </div>
      );
    }

    if (shippingQuoteStatus === 'failed') {
      return (
        <div className="flex flex-col gap-2 text-sm text-red-600">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {t('orderSummary.shippingErrorInline', 'We could not load shipping rates. Try again shortly.')}
          </div>
          <div className="text-xs text-muted-foreground">
            {shippingQuoteError ?? t('orderSummary.shippingErrorInlineHelper', 'The provider may be temporarily unavailable.')}
          </div>
          <Button variant="outline" size="sm" onClick={handleRefreshShippingQuote} className="w-fit">
            {t('orderSummary.shippingRetryButton', 'Retry fetching rates')}
          </Button>
        </div>
      );
    }

    const options = sortedShippingOptions;
    const selectedOptionKey = selectedShippingOptionKey ?? (options[0] ? getShippingOptionKey(options[0]) : null);
    const requiresOfficeSelection = novapostSupportsOfficeSelection && deliveryMode !== 'COURIER';

    const renderNovapostControls = () => {
      if (!novapostSupportsOfficeSelection) {
        return null;
      }

      return (
        <div className="flex flex-col gap-3 rounded-lg border border-primary/20 bg-muted/30 p-3">
          <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('orderSummary.placeOfReceiptLabel', 'Place of receipt')}
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={deliveryMode === 'COURIER' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleDeliveryModeChange('COURIER')}
            >
              {t('orderSummary.placeOfReceiptAddress', 'Address')}
            </Button>
            <Button
              variant={deliveryMode === 'BRANCH' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleDeliveryModeChange('BRANCH')}
            >
              {t('orderSummary.placeOfReceiptBranch', 'Branch')}
            </Button>
            <Button
              variant={deliveryMode === 'LOCKER' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleDeliveryModeChange('LOCKER')}
            >
              {t('orderSummary.placeOfReceiptLocker', 'Parcel locker')}
            </Button>
            <Button
              variant={deliveryMode === 'PICKUP_POINT' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleDeliveryModeChange('PICKUP_POINT')}
            >
              {t('orderSummary.placeOfReceiptPickupPoint', 'Pickup point')}
            </Button>
          </div>

          {requiresOfficeSelection && (
            <div className="relative">
              <SearchableSelect
                options={warehouseSelectOptions}
                value={selectedWarehouse?.id}
                onChange={(warehouseId) => {
                  const warehouse = warehouses.find((entry) => entry.id === warehouseId);
                  if (warehouse) {
                    handleWarehouseSelect(warehouse);
                  }
                }}
                placeholder={t('orderSummary.receiptPlaceSearchPlaceholder', 'Search for a receipt place...')}
                searchPlaceholder={t('orderSummary.receiptPlaceSearchPlaceholder', 'Search for a receipt place...')}
                noMatchesLabel={isLoadingWarehouses
                  ? t('orderSummary.loadingBranches', 'Loading branches...')
                  : t('orderSummary.noBranchesFound', 'No branches found')}
                disabled={isLoadingWarehouses && warehouseSelectOptions.length === 0}
                className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                menuClassName="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md"
                inputClassName="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                optionClassName="w-full border-b px-4 py-2 text-left text-sm transition-colors last:border-0 hover:bg-accent hover:text-accent-foreground"
                selectedOptionClassName="w-full border-b bg-accent px-4 py-2 text-left text-sm text-accent-foreground last:border-0"
              />
              {isLoadingWarehouses && warehouseSelectOptions.length > 0 ? (
                <div className="pointer-events-none absolute right-9 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : null}
            </div>
          )}
        </div>
      );
    };

    if (options.length === 0) {
      return (
        <div className="flex flex-col gap-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {t('orderSummary.shippingNoOptions', 'No shipping options were returned. Refresh to try again.')}
          </div>
          <Button variant="outline" size="sm" onClick={handleRefreshShippingQuote} className="w-fit">
            {t('orderSummary.shippingRetryButton', 'Retry fetching rates')}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3">
        <div className={`min-h-4 text-xs text-muted-foreground ${isRefreshingShippingOptions ? 'flex items-center gap-2' : 'invisible'}`}>
          {isRefreshingShippingOptions ? (
            <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t('orderSummary.shippingRefreshingInline', 'Refreshing delivery rates…')}
            </>
          ) : (
            <span>{t('orderSummary.shippingRefreshingInline', 'Refreshing delivery rates…')}</span>
          )}
        </div>
        {options.map((option) => {
          const provider = option.provider ?? shippingQuote?.provider ?? 'UKRPOSHTA';
          const optionKey = getShippingOptionKey(option);
          const isSelected = optionKey === selectedOptionKey;
          const comparableAmount = getComparableShippingAmount(option);
          const displayCurrency = orderCurrency;
          const priceLabel = formatCurrency(comparableAmount, displayCurrency);
          const packageTypeLabel = toPackageTypeLabel(t, option.packageType);
          const hasResolvedNovapostDestination = deliveryMode === 'COURIER'
            || !novapostSupportsOfficeSelection
            || Boolean(selectedWarehouse?.id || shippingAddress?.novaposhtaWarehouseId);
          const selectedDestinationLabel = provider === 'NOVAPOST' && isSelected
            ? (
                deliveryMode === 'COURIER'
                  ? formatDetailedDestinationAddress(shippingAddress)
                    ?? toReceiptPlaceLabel(t, deliveryMode)
                  : selectedWarehouse?.label
                    ? `${toReceiptPlaceLabel(t, deliveryMode)}: ${selectedWarehouse.label}`
                    : null
              )
            : null;
          const destinationStatusLabel = provider === 'NOVAPOST' && isSelected
            ? selectedDestinationLabel ?? (
                hasResolvedNovapostDestination
                  ? null
                  : t('orderSummary.novapostBranchSelectionWarning', 'Nova Post quote is estimated until a specific branch is selected.')
              )
            : null;
          const destinationStatusClassName = selectedDestinationLabel
            ? 'text-xs text-muted-foreground'
            : 'text-xs text-yellow-700';
          const visibleWarnings = provider === 'NOVAPOST' && hasResolvedNovapostDestination
            ? option.warnings?.filter((warning) => warning.trim() !== NOVAPOST_BRANCH_SELECTION_WARNING)
            : provider === 'NOVAPOST' && isSelected
              ? option.warnings?.filter((warning) => warning.trim() !== NOVAPOST_BRANCH_SELECTION_WARNING)
            : option.warnings;
          const estimatedDeliveryLabel = formatEstimatedDeliveryRange(
            t,
            option.eta ?? inferEstimatedDeliveryWindow(option, shippingAddress?.countryCode),
          );
          return (
            <div key={optionKey} className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => handleSelectShippingOption(option)}
                onDoubleClick={() => handleSelectShippingOptionAndHide(option)}
                className={`flex w-full flex-col gap-2 rounded-lg border p-4 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:flex-row sm:items-start sm:justify-between ${
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/40 hover:bg-muted/40'
                }`}
              >
                <div className="flex flex-1 flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      {option.option === 'STANDARD'
                        ? t('orderSummary.shippingOptionStandard', 'Standard delivery')
                        : t('orderSummary.shippingOptionExpress', 'Express delivery')}
                    </span>
                    <Badge variant="outline" className="uppercase tracking-wide">
                      {provider === 'NOVAPOST'
                        ? t('orderSummary.shippingProviderNovaPost', 'Nova Post')
                        : t('orderSummary.shippingProviderUkrposhta', 'Ukrposhta')}
                    </Badge>
                    {renderShippingOptionBadge(option)}
                  </div>
                  <div className="text-base font-semibold text-foreground">{priceLabel}</div>
                  {packageTypeLabel ? (
                    <div className="text-xs text-muted-foreground">
                      {t('orderSummary.shippingPackageTypeLabel', 'Package type')}: {packageTypeLabel}
                    </div>
                  ) : null}
                  {destinationStatusLabel ? (
                    <div className={destinationStatusClassName}>
                      {selectedDestinationLabel
                        ? `${t('orderSummary.selectedDestinationLabel', 'Selected destination')}: ${destinationStatusLabel}`
                        : destinationStatusLabel}
                    </div>
                  ) : null}
                  {visibleWarnings && visibleWarnings.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {visibleWarnings.map((warning) => {
                        const translatedWarning = translateShippingWarning(warning);
                        return (
                          <Badge key={warning} variant="outline" className="border-yellow-400 text-yellow-700">
                            {translatedWarning}
                          </Badge>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col items-end gap-1 text-right text-xs text-muted-foreground sm:w-48">
                  {estimatedDeliveryLabel ? (
                    <span className="inline-flex items-center gap-1" title={shippingEstimateTooltip}>
                      <span>{estimatedDeliveryLabel}</span>
                      <Info className="h-3.5 w-3.5" aria-hidden />
                    </span>
                  ) : null}
                  <span>
                    {isSelected
                      ? t('orderSummary.shippingSelected', 'Selected option')
                      : t('orderSummary.shippingSelectCta', 'Tap to select')}
                  </span>
                </div>
              </button>

              {isSelected && provider === 'NOVAPOST' ? renderNovapostControls() : null}
            </div>
          );
        })}
      </div>
    );
  };

  const handleWalletRefresh = useCallback(async () => {
    setIsLoadingBalance(true);
    try {
      await dispatch(loadWalletSummary()).unwrap();
    } catch (error) {
      logging.error('[OrderSummaryContainer] Failed to refresh wallet', { error });
    } finally {
      setIsLoadingBalance(false);
    }
  }, [dispatch]);

  const handleClosePaymentModal = useCallback(() => {
    setIsModalOpen(false);
    setIsPollingPayment(false);
  }, []);

  const handleTopUp = useCallback(async () => {
    if (!orderTotal || orderTotal <= 0) {
      return;
    }
    if (liqPayShortfall <= 0) {
      return;
    }
    setTopUpError(null);
    const desiredAmount = clampTopUpAmount(liqPayShortfall);
    const checkoutTarget = prepareLiqPayCheckoutTarget();
    setIsInitiatingTopUp(true);
    try {
      analytics.track('button: ui.order_summary_topup_click', { orderId, amount: desiredAmount, currency: liqPayCurrency });
      const checkout = await createWalletTopUpCheckout({
        amount: desiredAmount,
        currency: liqPayCurrency,
        language: resolveLiqPayLanguage(i18n.language),
        orderId: orderId ?? undefined,
      });
      submitLiqPayForm(checkout.data, checkoutTarget);
      setIsModalOpen(true);
      setIsPollingPayment(true);
      setTimeout(() => {
        void handleWalletRefresh();
      }, 5000);
    } catch (error) {
      checkoutTarget.handle?.close();
      logging.error('Failed to create wallet top-up checkout', { error });
      setTopUpError(t('wallet.topUpFailed', 'Failed to initiate top-up. Please try again.'));
    } finally {
      setIsInitiatingTopUp(false);
    }
  }, [analytics, handleWalletRefresh, i18n.language, liqPayCurrency, liqPayShortfall, orderId, orderTotal, t]);

  const selectedShippingFingerprint = useMemo(() => {
    if (!hasResolvedShippingCost) {
      return null;
    }
    const fingerprint = activeShippingOption?.fingerprint;
    if (!fingerprint || fingerprint.trim().length === 0) {
      return null;
    }
    const provider = activeShippingOption?.provider?.trim() ?? shippingQuote?.provider?.trim();
    if (!provider) {
      return null;
    }
    return {
      provider,
      option: activeShippingOption?.option ?? 'STANDARD',
      fingerprint,
    } as const;
  }, [activeShippingOption?.fingerprint, activeShippingOption?.option, hasResolvedShippingCost, shippingQuote?.provider]);

  const confirmPayment = useCallback(async () => {
    if (!orderId || isConfirming) {
      return;
    }
    analytics.track('button: ui.order_summary_confirm_click', { orderId, source: 'wallet_confirm_button' });
    // TODO: remove temporary logging instrumentation once order cleanup is verified.
    logging.info('orderSummary.confirmPayment.init', {
      orderId,
      itemStateBefore: orderItems.map((item) => ({
        id: item?.id,
        orderId: item?.orderId,
        status: item?.status,
      })),
    });
    setConfirmError(null);
    setIsConfirming(true);
    let skippedWithdraw = false;
    try {
      try {
        await withdrawOrderBalance(orderId, {
          shippingSelection: selectedShippingFingerprint ?? undefined,
          promoCodes: appliedPromoCodes,
        });
      } catch (err) {
        const isAlreadyCharged = (() => {
          if (!isAxiosError(err) || err.response?.status !== 409) {
            return false;
          }
          const data = err.response?.data;
          const message = typeof data === 'string'
            ? data
            : typeof data?.message === 'string'
              ? data.message
              : '';
          return message.toLowerCase().includes('already been charged');
        })();

        if (!isAlreadyCharged) {
          throw err;
        }

        skippedWithdraw = true;
        analytics.track('system: ui.order_summary_withdraw_already_charged', { orderId });
      }

      try {
        const shippingAddressPayload = shippingAddress
          ? buildCheckoutContactPayload({
              ...shippingAddress,
              novaposhtaWarehouseId: selectedWarehouse?.id || shippingAddress.novaposhtaWarehouseId,
              deliveryMode: deliveryMode || shippingAddress.deliveryMode,
              shippingProvider: activeShippingOption?.provider ?? shippingQuote?.provider ?? shippingAddress.shippingProvider,
            }, fulfillmentMode)
          : undefined;

        await transitionOrderStatusForUser(orderId, 'PAID', {
          shippingAddress: shippingAddressPayload,
          fulfillmentMode,
        });
      } catch (err) {
        const isAlreadyPaid = (() => {
          if (!isAxiosError(err)) {
            return false;
          }
          const status = err.response?.status;
          const data = err.response?.data;
          const message = typeof data === 'string'
            ? data
            : typeof data?.message === 'string'
              ? data.message
              : '';
          if (status === 409) {
            return message.toLowerCase().includes('cannot transition order status backwards')
              || message.toLowerCase().includes('order has already been marked as paid');
          }
          if (status === 422) {
            return message.toLowerCase().includes('unsupported order status transition');
          }
          return false;
        })();

        if (!isAlreadyPaid) {
          throw err;
        }

        analytics.track('system: ui.order_summary_transition_already_paid', { orderId });
      }
    } catch (error) {
      logging.error('Failed to confirm order payment', { error });
      setConfirmError(t('orderSummary.confirmationFailed', 'We could not confirm your order. Please try again.'));
      setIsConfirming(false);
      return;
    }

    dispatch(completeOrderFulfillment());

    const { order: refreshedOrderSliceState } = store.getState();
    const sanitizedItems: Order['items'] = Array.isArray(refreshedOrderSliceState.items)
      ? refreshedOrderSliceState.items.map(item => {
          const normalized: Partial<Order['items'][number]> & { id: string } = { id: item.id };

          if (typeof item.uploadedFileId === 'string' && item.uploadedFileId.trim().length > 0) {
            normalized.uploadedFileId = item.uploadedFileId.trim();
          }
          if (typeof item.paperTypeId === 'string' && item.paperTypeId.trim().length > 0) {
            normalized.paperTypeId = item.paperTypeId.trim();
          }
          if (typeof item.printerId === 'string' && item.printerId.trim().length > 0) {
            normalized.printerId = item.printerId.trim();
          }
          if (typeof item.orderId === 'string' && item.orderId.trim().length > 0) {
            normalized.orderId = item.orderId.trim();
          }

          const widthIn = typeof item.widthIn === 'number' && Number.isFinite(item.widthIn) && item.widthIn > 0
            ? item.widthIn
            : null;
          const heightIn = typeof item.heightIn === 'number' && Number.isFinite(item.heightIn) && item.heightIn > 0
            ? item.heightIn
            : null;
          const marginIn = typeof item.marginIn === 'number' && Number.isFinite(item.marginIn) && item.marginIn > 0
            ? item.marginIn
            : null;
          const galleryWrapIn = typeof item.galleryWrapIn === 'number' && Number.isFinite(item.galleryWrapIn) && item.galleryWrapIn > 0
            ? item.galleryWrapIn
            : null;

          if (widthIn != null) {
            normalized.widthIn = widthIn;
          }
          if (heightIn != null) {
            normalized.heightIn = heightIn;
          }
          if (marginIn != null) {
            normalized.marginIn = marginIn;
          }
          if (galleryWrapIn != null) {
            normalized.galleryWrapIn = galleryWrapIn;
          }

          const originalWidthPx = typeof item.originalWidthPx === 'number' && Number.isFinite(item.originalWidthPx) && item.originalWidthPx > 0
            ? item.originalWidthPx
            : null;
          const originalHeightPx = typeof item.originalHeightPx === 'number' && Number.isFinite(item.originalHeightPx) && item.originalHeightPx > 0
            ? item.originalHeightPx
            : null;
          const originalWidthIn = typeof item.originalWidthIn === 'number' && Number.isFinite(item.originalWidthIn) && item.originalWidthIn > 0
            ? item.originalWidthIn
            : null;
          const originalHeightIn = typeof item.originalHeightIn === 'number' && Number.isFinite(item.originalHeightIn) && item.originalHeightIn > 0
            ? item.originalHeightIn
            : null;

          if (originalWidthPx != null) {
            normalized.originalWidthPx = originalWidthPx;
          }
          if (originalHeightPx != null) {
            normalized.originalHeightPx = originalHeightPx;
          }
          if (originalWidthIn != null) {
            normalized.originalWidthIn = originalWidthIn;
          }
          if (originalHeightIn != null) {
            normalized.originalHeightIn = originalHeightIn;
          }

          const dpiX = typeof item.dpiX === 'number' && Number.isFinite(item.dpiX) && item.dpiX > 0 ? item.dpiX : null;
          const dpiY = typeof item.dpiY === 'number' && Number.isFinite(item.dpiY) && item.dpiY > 0 ? item.dpiY : null;

          if (dpiX != null) {
            normalized.dpiX = dpiX;
          }
          if (dpiY != null) {
            normalized.dpiY = dpiY;
          }

          const normalizedStatus = typeof item.status === 'string'
            ? item.status.trim().toUpperCase()
            : '';
          if (
            normalizedStatus === 'IN_ORDER' ||
            normalizedStatus === 'READY_TO_PRINT' ||
            normalizedStatus === 'PRINTED'
          ) {
            normalized.status = normalizedStatus;
          } else if (normalizedStatus === 'NEW') {
            normalized.status = 'NEW';
          }

          if (item.price && typeof item.price.value === 'number' && Number.isFinite(item.price.value) && typeof item.price.currency === 'string') {
            normalized.price = {
              value: item.price.value,
              currency: item.price.currency,
            };
          }

          if (typeof item.pricingStatus === 'string' && item.pricingStatus.length > 0) {
            normalized.pricingStatus = item.pricingStatus;
          }
          if (item.pricingError === null || typeof item.pricingError === 'string') {
            normalized.pricingError = item.pricingError;
          }

          if (item.createdAt) {
            if (typeof item.createdAt === 'string') {
              normalized.createdAt = item.createdAt;
            } else if (item.createdAt instanceof Date && !Number.isNaN(item.createdAt.valueOf())) {
              normalized.createdAt = item.createdAt.toISOString();
            }
          }
          if (item.updatedAt) {
            if (typeof item.updatedAt === 'string') {
              normalized.updatedAt = item.updatedAt;
            } else if (item.updatedAt instanceof Date && !Number.isNaN(item.updatedAt.valueOf())) {
              normalized.updatedAt = item.updatedAt.toISOString();
            }
          }

          return normalized as Order['items'][number];
        })
      : [];

    sendToSupervisor({
      type: 'ORDER.APPLY_PATCH',
      patch: {
        items: sanitizedItems,
      },
    });
    sendToSupervisor({ type: 'ORDER.CLEAR_FOR_ORDER', orderId, skipSetActiveStep: true });
    sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'done' });

    logging.info('orderSummary.confirmPayment.afterCleanup', {
      orderId,
      itemStateAfter: refreshedOrderSliceState.items.map((item) => ({
        id: item?.id,
        orderId: item?.orderId,
        status: item?.status,
      })),
    });

    try {
      sendToSupervisor({
        type: 'UI.REQUEST_AI_PROMPT',
        aiPrompt: 'Let the user know their order has been confirmed, charged from wallet, and moved to the order list. Keep it concise.',
        fallbackMessage: t('orderSummary.confirmationFallback', 'Your order has been confirmed and moved to the order list.'),
      });
    } catch (supervisorError) {
      logging.warn('[OrderSummaryContainer] Failed to notify supervisor', { error: supervisorError });
    }

    analytics.track('system: ui.order_summary_confirm_success', {
      orderId,
      skippedWithdraw,
    });

    setIsConfirming(false);
  }, [
    activeShippingOption?.fingerprint,
    analytics,
    appliedPromoCodes,
    dispatch,
    isConfirming,
    fulfillmentMode,
    isPickupMode,
    orderId,
    selectedShippingFingerprint,
    sendToSupervisor,
    shippingAddress,
    t,
  ]);

  useEffect(() => {
    if (!isModalOpen || !isPollingPayment) {
      return undefined;
    }
    const id = window.setInterval(() => {
      void handleWalletRefresh();
    }, 5000);
    return () => window.clearInterval(id);
  }, [handleWalletRefresh, isModalOpen, isPollingPayment]);

  useEffect(() => {
    if (isModalOpen && hasEnoughBalance) {
      setIsModalOpen(false);
      setIsPollingPayment(false);
      setIsBalanceReadyPromptOpen(true);
    }
  }, [hasEnoughBalance, isModalOpen]);

  const handleRefreshClick = useCallback(() => {
    analytics.track('button: ui.order_summary_refresh_balance_click', { orderId });
    void handleWalletRefresh();
  }, [analytics, handleWalletRefresh, orderId]);

  const activeCollection = orderedItems.length > 0 ? orderedItems : (orderItems ?? []);
  const activeIndex = activeCollection.findIndex((entry) => entry?.id === activeOrderedId);

  const activeItemLabel = activeIndex >= 0 && activeCollection.length > 1
    ? (orderedItems.length > 0
      ? t('orderSummary.activeOrderedItem', 'Viewing ordered item {{index}} of {{total}}', {
        index: activeIndex + 1,
        total: activeCollection.length,
      })
      : t('orderSummary.activeItem', 'Viewing item {{index}} of {{total}}', {
        index: activeIndex + 1,
        total: activeCollection.length,
      }))
    : undefined;

  const priceLabel = getStoredPricingLabel({ item: resolvedItem ?? null, t });
  const quantityValue = typeof resolvedItem?.quantity === 'number' && Number.isFinite(resolvedItem.quantity)
    ? Math.min(99, Math.max(1, Math.round(resolvedItem.quantity)))
    : 1;
  const quantityLabel = String(quantityValue);
  const quantityCostLabel = quantityValue > 1
    && resolvedItem?.price
    && typeof resolvedItem.price.value === 'number'
    && Number.isFinite(resolvedItem.price.value)
    ? formatCurrency(resolvedItem.price.value * quantityValue, resolvedItem.price.currency)
    : null;

  let orderTotalLabel: string | null = null;
  let orderTotalHelper: string | null = null;
  let itemsSubtotalLabel: string | null = null;
  let itemsSubtotalHelper: string | null = null;
  let userDiscountLabel: string | null = null;
  let userDiscountHelper: string | null = null;

  if (pricingSummary.pricedCount > 0 && !pricingSummary.hasMismatch && !pricingSummary.hasInvalid && pricingSummary.currency) {
    try {
      const formatter = new Intl.NumberFormat(i18n.language ?? undefined, {
        style: 'currency',
        currency: pricingSummary.currency,
      });
      itemsSubtotalLabel = formatter.format(pricingSummary.total);
    } catch (error) {
      logging.warn('Failed to format items subtotal', { error });
      itemsSubtotalLabel = pricingSummary.total.toFixed(2);
    }

    if (pricingSummary.pricedCount < pricingSummary.totalItems || pricingSummary.hasUnpricedItems) {
      itemsSubtotalHelper = t('orderSummary.itemsSubtotalPartial', 'Subtotal reflects {{priced}} of {{total}} items.', {
        priced: pricingSummary.pricedCount,
        total: pricingSummary.totalItems,
      });
    }
  } else {
    if (pricingSummary.hasMismatch) {
      itemsSubtotalHelper = t('orderSummary.totalCurrencyConflict', 'Resolve currency mismatches to view total.');
    } else if (pricingSummary.pricedCount === 0) {
      itemsSubtotalHelper = t('orderSummary.totalMissing', 'Add pricing to items to see total.');
    } else if (pricingSummary.hasInvalid || pricingSummary.hasUnpricedItems) {
      itemsSubtotalHelper = t('orderSummary.totalMissing', 'Add pricing to items to see total.');
    }
  }

  if (itemsSubtotalLabel) {
    try {
      const currency = pricingSummary.currency ?? shippingCurrency ?? 'USD';
      const formatter = new Intl.NumberFormat(i18n.language ?? undefined, {
        style: 'currency',
        currency,
      });
      const promoDiscountTotal = promotionPreview?.promotionDiscountTotalUsd ?? 0;
      orderTotalLabel = formatter.format(Math.max(0, pricingSummary.total + shippingCost - promoDiscountTotal));
    } catch (error) {
      logging.warn('Failed to format order total', { error });
      const promoDiscountTotal = promotionPreview?.promotionDiscountTotalUsd ?? 0;
      orderTotalLabel = Math.max(0, pricingSummary.total + shippingCost - promoDiscountTotal).toFixed(2);
    }
  } else {
    orderTotalHelper = itemsSubtotalHelper;
  }

  if (
    userDiscountPercent != null
    && userDiscountPercent > 0
    && pricingSummary.total > 0
    && pricingSummary.currency
  ) {
    const clampedPercent = Math.max(0, Math.min(100, userDiscountPercent));
    const discountMultiplier = 1 - (clampedPercent / 100);
    const discountAmount = discountMultiplier > 0
      ? roundCurrencyValue((pricingSummary.total / discountMultiplier) - pricingSummary.total)
      : roundCurrencyValue(pricingSummary.total);

    if (discountAmount > 0) {
      const formattedDiscountAmount = formatCurrency(discountAmount, pricingSummary.currency);
      userDiscountLabel = t('orderSummary.userDiscountValue', '{{percent}}% (-{{amount}})', {
        percent: clampedPercent,
        amount: formattedDiscountAmount,
      });
      userDiscountHelper = t('orderSummary.userDiscountHelper', 'Included in items subtotal.');
    }
  }

  const appliedPromoCodeLabels = promotionPreview?.redemptions.map((redemption) => ({
    code: redemption.code,
    discountLabel: `-${formatCurrency(redemption.discountAmountUsd, pricingSummary.currency ?? 'USD')}`,
  })) ?? [];

  const isOrderPreparingForPayment = !orderId;

  return (
    <div className="flex w-full flex-col space-y-6">
      <div className="mx-auto flex w-full max-w-5xl flex-col space-y-6">
        {orderedHeaderOptions.length > 0 ? (
          <ItemSelectionHeader
            title={t('payment.orderedItemsHeader', 'Ordered items')}
            items={orderedHeaderOptions}
            activeKey={activeOrderedId}
            onSelect={(key) => setActiveOrderedId(key)}
          />
        ) : null}
        <div className="flex w-full justify-center">
          <OrderSummary
            paperType={paperTypeName || effectivePaperTypeId || undefined}
        printer={printerName || effectivePrinterId || undefined}
        width={width}
        height={height}
        imageId={imageId}
        imageUrl={imageUrl}
        originalWidthPx={resolvedItem?.originalWidthPx ?? props.originalWidthPx ?? (imageDims.width ?? undefined)}
        originalHeightPx={resolvedItem?.originalHeightPx ?? props.originalHeightPx ?? (imageDims.height ?? undefined)}
        widthIn={widthInchesStored}
        heightIn={heightInchesStored}
        marginIn={resolvedItem?.marginIn ?? summaryPrimary?.marginIn ?? null}
        galleryWrapIn={resolvedItem?.galleryWrapIn ?? summaryPrimary?.galleryWrapIn ?? null}
        shippingAddressText={shippingAddressText}
        isPaperConfigured={isPaperConfigured}
        isPrinterConfigured={isPrinterConfigured}
        isPrintSizeConfigured={isPrintSizeConfigured}
        hasShippingAddress={hasShippingAddress}
        priceLabel={priceLabel}
        quantityLabel={quantityLabel}
        quantityCostLabel={quantityCostLabel}
        printQualityWarning={resolvedItem?.printQualityWarning ?? null}
        activeItemLabel={activeItemLabel}
        orderTotalLabel={orderTotalLabel}
        orderTotalHelper={orderTotalHelper}
        itemsSubtotalLabel={itemsSubtotalLabel}
        itemsSubtotalHelper={itemsSubtotalHelper}
        userDiscountLabel={userDiscountLabel}
        userDiscountHelper={userDiscountHelper}
        promoCodeControl={{
          isVisible: isPromoInputVisible,
          code: promoCode,
          isApplying: isApplyingPromoCode,
          errorMessage: promoCodeError,
          appliedCodes: appliedPromoCodeLabels,
          onShow: () => {
            setIsPromoInputVisible(true);
            setPromoCodeError(null);
          },
          onCodeChange: (value) => {
            setPromoCode(value);
            if (promoCodeError) {
              setPromoCodeError(null);
            }
          },
          onApply: () => {
            void handleApplyPromoCode();
          },
          onRemove: handleRemovePromoCode,
        }}
        shippingCostLabel={shippingCostLabel}
        shippingCostSummary={shippingCostSummary}
        shippingCostHelper={shippingCostHelper}
        shippingOptionsToggle={!isPickupMode ? {
          isVisible: showShippingOptions,
          onToggle: toggleShippingOptions,
          showWarning: showShippingOptionsWarning,
        } : undefined}
        shippingOptionsContent={!isPickupMode && showShippingOptions ? (
          <Card className="mt-3">
            <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1">
                <CardTitle>{t('orderSummary.shippingOptionsTitle', 'Shipping options')}</CardTitle>
                <CardDescription>
                  {t('orderSummary.shippingOptionsSubtitle', 'Select a delivery speed to include shipping in your total.')}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshShippingQuote}
                  disabled={shippingQuoteStatus === 'loading'}
                >
                  {shippingQuoteStatus === 'loading'
                    ? t('orderSummary.shippingRefreshLoading', 'Refreshing…')
                    : t('orderSummary.shippingRefreshButton', 'Refresh rates')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={toggleShippingOptions}
                >
                  {t('orderSummary.shippingOptionsHide', 'Hide')}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderShippingOptions()}
              {packagingSummary ? (
                <div className="flex items-start gap-3 rounded-md border border-dashed border-muted p-3 text-sm text-muted-foreground">
                  <PackageIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium text-foreground">
                      {t('orderSummary.shippingPackagingLabel', 'Packaging ready')} • {Math.round(packagingSummary.totalWeightGrams)}g
                    </div>
                    <div className="text-xs">
                      {t('orderSummary.shippingPackagingDimensions', 'Dimensions {{length}} × {{width}} × {{height}} {{unit}} · Type {{type}}', {
                        length: convertInchesToUnit(packagingSummary.packageDimensions.lengthIn, measurementUnit)
                          .toFixed(measurementPrecision),
                        width: convertInchesToUnit(packagingSummary.packageDimensions.widthIn, measurementUnit)
                          .toFixed(measurementPrecision),
                        height: convertInchesToUnit(packagingSummary.packageDimensions.heightIn, measurementUnit)
                          .toFixed(measurementPrecision),
                        unit: measurementUnitLabel,
                        type: packagingSummary.packagingType.toUpperCase(),
                      })}
                    </div>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        ) : null}
          paymentSection={{
          orderId,
          balanceMessage: orderCurrency === liqPayCurrency
            ? t(
              'orderSummary.balanceSummary',
              'Wallet balance: {{balance}} · Order total: {{total}}',
              {
                balance: formattedLiqPayWalletBalance,
                total: formattedLiqPayOrderTotal,
              },
            )
            : t(
              'orderSummary.balanceSummaryWithBase',
              'Wallet balance: {{balance}} · Order total: {{total}} (≈ {{converted}})',
              {
                balance: formattedLiqPayWalletBalance,
                total: formattedLiqPayOrderTotal,
                converted: formattedOrderTotalBaseCurrency,
              },
            ),
          shortfallMessage: liqPayShortfall > 0
            ? t('orderSummary.balanceShortfall', 'You need to pay {{amount}} to proceed.', {
              amount: formattedLiqPayShortfall,
            })
            : null,
          helperText: isOrderPreparingForPayment
            ? t('orderSummary.preparingOrderHelper', 'Preparing your order for payment. Please wait a moment.')
            : liqPayPaymentHelperText,
          summaryMessage: isOrderPreparingForPayment
            ? t('orderSummary.preparingOrderSummary', 'We are creating your order record before payment can be confirmed.')
            : hasEnoughBalance
            ? t('orderSummary.walletSufficientMessage', 'Payment is ready. Press Confirm order to send your files to printing.')
            : t(
              'orderSummary.walletInsufficientMessage',
              'Add funds to your wallet to cover the order total then press Confirm order button.',
              ),
          statusLabel: isOrderPreparingForPayment
            ? t('orderSummary.preparingOrderStatus', 'Order setup in progress.')
            : hasEnoughBalance
            ? t('orderSummary.walletBalanceStatus', 'Wallet balance sufficient for payment.')
            : t('orderSummary.walletShortfallStatus', 'Wallet balance is insufficient.'),
          errorMessage: confirmError ?? topUpError,
          currencyControl: paymentCurrencyControl,
          exchangeRateInfo: paymentExchangeRateInfo,
          refreshAction: {
            label: t('wallet.refreshBalance', 'Refresh balance'),
            onClick: handleRefreshClick,
            disabled: isLoadingBalance,
            loading: isLoadingBalance,
          },
          topUpAction: liqPayShortfall > 0
            ? {
                label: t('orderSummary.topUpPayButton', 'Pay {{amount}} to top up', { amount: formattedLiqPayShortfall }),
                onClick: handleTopUp,
                disabled: isOrderPreparingForPayment
                  || isInitiatingTopUp
                  || isLoadingBalance
                  || orderConversion.status === 'loading'
                  || walletConversion.status === 'loading'
                  || isWaitingForShippingQuote,
                loading: isInitiatingTopUp,
              }
            : undefined,
          confirmAction: {
            label: t('orderSummary.confirmOrderButton', 'Confirm order'),
            onClick: confirmPayment,
            disabled: isOrderPreparingForPayment || !hasEnoughBalance || isConfirming || isLoadingBalance,
            loading: isConfirming,
            highlight: !isOrderPreparingForPayment && hasEnoughBalance && !isConfirming && !isLoadingBalance,
          },
          }}
          />
        </div>
      </div>
      <PaymentInstructionsModal
        open={Boolean(orderId) && isModalOpen}
        onRefresh={handleWalletRefresh}
        onClose={handleClosePaymentModal}
        isRefreshing={isLoadingBalance || isPollingPayment}
        refreshLabel={t('wallet.refreshBalance', 'Refresh balance')}
        refreshingLabel={t('wallet.refreshingBalance', 'Refreshing…')}
        closeLabel={t('common.close', 'Close')}
        title={t('orderSummary.waitingForPayment', 'Waiting for payment confirmation')}
        description={t('orderSummary.waitingForPaymentDescription', 'Once the payment provider confirms your top-up, your wallet balance will update automatically.')}
      />
      <ConfirmAlertDialog
        open={Boolean(orderId) && isBalanceReadyPromptOpen}
        onOpenChange={setIsBalanceReadyPromptOpen}
        title={t('orderSummary.balanceReadyDialogTitle', 'Wallet balance is ready')}
        description={t(
          'orderSummary.balanceReadyDialogDescription',
          'You now have enough money to confirm your order and move it to production. Please press Confirm order.',
        )}
        confirmText={t('orderSummary.confirmOrderButton', 'Confirm order')}
        cancelText={t('common.close', 'Close')}
        onConfirm={confirmPayment}
      />
    </div>
  );
};

export default OrderSummaryContainer;
